import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

// API base configuration
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000';

interface UserSummary {
  user_id: string;
  email: string;
  name: string;
  auth_provider: string;
  last_login?: string;
  trip_count: number;
}

interface ImpersonationResponse {
  impersonation_token: string;
  target_user: any;
  expires_at: string;
  impersonated_by: string;
}

const AdminPanel: React.FC = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [users, setUsers] = useState<UserSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [impersonating, setImpersonating] = useState(false);

  const showAdminPanel = (): boolean => {
    // Must be on localhost
    if (window.location.hostname !== 'localhost') {
      console.log('🔍 AdminPanel: Not on localhost, hiding panel');
      return false;
    }
    
    // Must have admin query parameter
    const adminParam = searchParams.get('admin');
    if (adminParam !== 'true') {
      console.log('🔍 AdminPanel: No admin=true query param, hiding panel');
      return false;
    }
    
    console.log('✅ AdminPanel: Conditions met, showing panel');
    return true;
  };

  const fetchUsers = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const url = `${API_BASE_URL}/api/admin/users?admin=true`;
      console.log('🔍 AdminPanel: Fetching users from:', url);
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('google_oauth_token')}`
        }
      });
      
      console.log('🔍 AdminPanel: Response status:', response.status);
      console.log('🔍 AdminPanel: Response headers:', response.headers);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.log('❌ AdminPanel: Error response:', errorText);
        throw new Error(`Failed to fetch users: ${response.status} ${response.statusText}`);
      }
      
      const userData = await response.json();
      console.log('✅ AdminPanel: Users data:', userData);
      setUsers(userData);
    } catch (err: any) {
      console.error('❌ AdminPanel: Fetch error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const impersonateUser = async (targetUserId: string) => {
    setImpersonating(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/impersonate?admin=true`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('google_oauth_token')}`
        },
        body: JSON.stringify({
          target_user_id: targetUserId,
          reason: 'Admin testing'
        })
      });
      
      if (!response.ok) {
        throw new Error(`Failed to impersonate user: ${response.statusText}`);
      }
      
      const impersonationData: ImpersonationResponse = await response.json();
      
      // Store the impersonation token
      localStorage.setItem('google_oauth_token', impersonationData.impersonation_token);
      
      // Reload the page to apply the impersonation
      window.location.reload();
      
    } catch (err: any) {
      setError(err.message);
    } finally {
      setImpersonating(false);
    }
  };

  const stopImpersonation = () => {
    // Remove impersonation token and reload
    localStorage.removeItem('google_oauth_token');
    window.location.reload();
  };

  useEffect(() => {
    // Only fetch users if admin panel should be shown (admin=true query param)
    if (showAdminPanel()) {
      console.log('🔍 AdminPanel: Admin panel visible, fetching users...');
      fetchUsers();
    } else {
      console.log('🔍 AdminPanel: Admin panel not visible, skipping user fetch');
    }
  }, [searchParams]); // Re-run when search params change

  // Only show admin panel if user is authenticated and conditions are met
  if (!user || !showAdminPanel()) {
    return null;
  }

  // Check if currently impersonating
  const isImpersonating = user?.attributes?.email?.includes('impersonated-');

  return (
    <div className="fixed top-4 right-4 bg-white border border-gray-300 rounded-lg shadow-lg p-4 max-w-md z-50">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-800">Admin Panel</h3>
        {isImpersonating && (
          <button
            onClick={stopImpersonation}
            className="px-3 py-1 bg-red-500 text-white rounded text-sm hover:bg-red-600"
          >
            Stop Impersonation
          </button>
        )}
      </div>

      {isImpersonating && (
        <div className="mb-4 p-2 bg-yellow-100 border border-yellow-300 rounded text-sm">
          <strong>⚠️ Impersonating:</strong> You are currently impersonating a user
        </div>
      )}

      {error && (
        <div className="mb-4 p-2 bg-red-100 border border-red-300 rounded text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="mb-4">
        <button
          onClick={fetchUsers}
          disabled={loading}
          className="w-full px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50"
        >
          {loading ? 'Loading...' : 'Refresh Users'}
        </button>
      </div>

      <div className="max-h-96 overflow-y-auto">
        {users.map((user) => (
          <div key={user.user_id} className="border-b border-gray-200 py-2 last:border-b-0">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <div className="font-medium text-gray-800">{user.name}</div>
                <div className="text-sm text-gray-600">{user.email}</div>
                <div className="text-xs text-gray-500">
                  {user.auth_provider} • {user.trip_count} trips
                </div>
              </div>
              <button
                onClick={() => impersonateUser(user.user_id)}
                disabled={impersonating}
                className="ml-2 px-3 py-1 bg-green-500 text-white rounded text-sm hover:bg-green-600 disabled:opacity-50"
              >
                Impersonate
              </button>
            </div>
          </div>
        ))}
      </div>

      {users.length === 0 && !loading && (
        <div className="text-center text-gray-500 py-4">
          No users found
        </div>
      )}
    </div>
  );
};

export default AdminPanel;
